import { HttpClient, HttpHeaders } from "@angular/common/http";
import { EventEmitter, Injectable } from "@angular/core";
import { environment } from "../../environments/environment";
import { Observable, of, Subject } from "rxjs";
import {
  btoaUTF8,
  atobUTF8,
} from "../pages/markdown-editor/markdown-editor.service";
import { Node } from "../models/node.model";

@Injectable({
  providedIn: "root",
})
export class GithubService {
  private apiUrl = environment.githubApiUrl;
  private accessToken = "";
  private user: any;
  events: any[] = [];
  resyncNeeded = new EventEmitter();

  get owner() {
    return localStorage.getItem("ghostOwner") || this.user.login;
  }

  get repo() {
    return localStorage.getItem("selectedRepo");
  }

  get branch() {
    return localStorage.getItem("selectedBranch");
  }

  get wikiUrl() {
    return `https://github.com/${this.repo}.wiki.git`;
  }

  getAccessToken() {
    return this.accessToken;
  }

  get headers() {
    if (!this.accessToken) {
      throw new Error("No access token found");
    }
    return new HttpHeaders({
      Authorization: `Bearer ${this.accessToken}`,
    });
  }

  constructor(private http: HttpClient) {
    this.accessToken = localStorage.getItem("github_token") || "";
    if (this.accessToken) {
      this.user = JSON.parse(localStorage.getItem("github_user") || "null");
      if (!this.user) {
        this.getUser();
      }
    }
  }

  listenToUserEvents() {
    // listen to event stream in user.event_url
    this.http
      .get(`https://api.github.com/events`, { headers: this.headers })
      .subscribe((e: any) => {
        this.events.push(...e);
      });
  }

  async getUser() {
    if (localStorage.getItem("github_user")) {
      return JSON.parse(localStorage.getItem("github_user") || "null");
    }
    const user = await this.http
      .get(`${this.apiUrl}/user`, { headers: this.headers })
      .toPromise();
    this.user = user;
    localStorage.setItem("github_user", JSON.stringify(user));
    return user;
  }

  authenticate(code: string) {
    const url = `https://noobit-407508.uc.r.appspot.com/proxy/https://github.com/login/oauth/access_token`; // `https://noobit-407508.uc.r.appspot.com/proxy/https://github.com/login/oauth/access_token`;
    const body = {
      client_id: environment.githubClientId,
      client_secret: environment.githubClientSecret,
      code: code,
      redirect_uri: environment.githubRedirectUri,
    };

    const headers = new HttpHeaders({
      Accept: "application/json",
    });

    return this.http.post(url, body, { headers });
    // Scambio il codice OAuth per un token di accesso
  }

  setAccessToken(token: string) {
    this.accessToken = token;
  }

  getUserRepos() {
    return this.http.get(`https://api.github.com/user/repos`, {
      headers: this.headers,
    });
  }

  getRepoContent(path: string) {
    if (!this.repo) {
      const node = new Node();
      node.content = '# Example content\n\nThis is an example file';
      return of({
        sha: null,
        content: btoaUTF8(node.content),
      });
    }
    return this.http.get(`${this.apiUrl}/repos/${this.repo}/contents/${path}?nonce=${Math.random()}`, {
      headers: this.headers,
    });
  }

  getRepoTree(branch: string | null) {
    branch = branch || localStorage.getItem("selectedBranch");

    if (!branch || !this.repo || !this.owner) {
      return;
    }

    return this.http.get(
      `${this.apiUrl}/repos/${
        this.repo
      }/git/trees/${branch}?recursive=1&nonce=${Math.random()}`,
      { headers: this.headers }
    );
  }

  blame(path: string) {
    if (!this.repo) {
      return of(null);
    }
    return this.http.get(
      `${this.apiUrl}/repos/${this.repo}/commits?path=${path}`,
      {
        headers: this.headers,
      }
    );
  }

  async updateFile(
    path: string,
    content: string,
    sha: string,
    pr = false,
    isBase64 = false
  ): Promise<Observable<any>> {

    // content = this.cleanContentForBtoa(content);
    return new Promise(async (resolve, reject) => {
      const headers = new HttpHeaders({
        Authorization: `Bearer ${this.accessToken}`,
        Accept: "application/vnd.github.v3+json",
      });

      if (!pr) {
        content = isBase64 ? content : btoaUTF8(content);

        // regular update to the selected branch
        const body = {
          message: `Update ${path}`,
          content, // Base64 encode
          sha,
        };

        return resolve(
          this.http.put(
            `${this.apiUrl}/repos/${this.repo}/contents/${path}`,
            body,
            {
              headers,
            }
          )
        );
      } else {
        resolve(
          await this.createPR(
            "update-" + Date.now(),
            [{
              path,
              content: isBase64 ? atobUTF8(content) : content,
            }],
            `Update ${path}`,
            `This PR updates ${path}`
          )
        );
      }
    });
  }

  getCommitsByBranch( branch) {
    const url = `https://api.github.com/repos/${this.repo}/commits?sha=${branch}&per_page=100&nonce=${Math.random()}`;
    return this.http.get(url, {
      headers: this.headers
    })
  }


  async getCommits() {
    const url = `https://api.github.com/repos/${this.repo}/commits?per_page=100&nonce=${Math.random()}`;
    return await this.http.get(url, {
      headers: this.headers
    }).toPromise() as any;
  }


  async createOrUpdateFile(
    path: string,
    content: string,
    isBase64 = false
  ) {
    if (!this.repo) {
      return null
    }

    try {
      const result: any = await this.getRepoContent(path).toPromise();
      return this.updateFile(path, content, result.sha, false, isBase64);
    } catch (e) {
      const body = {
        message: `Create ${path}`,
        content: isBase64 ? content : btoa(content), // Base64 encode
      };
      return this.http.put(
        `${this.apiUrl}/repos/${this.repo}/contents/${path}`,
        body,
        { headers: this.headers }
      );
    }
  }

  getRepoBranches(repo) {
    return this.http.get<any>(`${this.apiUrl}/repos/${repo}/branches?nonce=${Math.random()}`, {
      headers: this.headers,
    });
  }

  deleteFile(path: string, sha: string) {
    const branch = localStorage.getItem("selectedBranch");

    if (!this.owner || !this.repo) {
      return;
    }
    const body = {
      message: `Delete ${path}`,
      sha,
      branch,
    };
    const obs = this.http
      .delete(`${this.apiUrl}/repos/${this.repo}/contents/${path}`, {
        headers: this.headers,
        body,
      })
      .toPromise();
    this.resyncNeeded.emit({
      path,
      type: 'delete'
    });


    obs.then(() => {
      this.resyncNeeded.emit({
        path,
        type: 'delete'
      });
    });
    return obs;
  }

  renameFile(path: string, newPath: string) {
    const branch = localStorage.getItem("selectedBranch");
    if (!this.owner || !this.repo) {
      return;
    }
    const body = {
      message: `Rename ${path}`,
      sha: null,
      content: null,
      branch,
    };
    const obs = this.http
      .put(`${this.apiUrl}/repos/${this.repo}/contents/${path}`, body, {
        headers: this.headers,
      })
      .toPromise();
    obs.then(() => {
      this.resyncNeeded.emit();
    });
    return obs;
  }

  searchFileInRepo(fuzzyName: string) {
    if (!this.owner || !this.repo) {
      return;
    }
    const apiUrl = `https://api.github.com/search/code?q=${fuzzyName}+in:path+repo:${this.repo}`;

    return this.http.get(apiUrl, { headers: this.headers }).toPromise();
  }

  async checkIfUserIsCollaborator(owner: string, repo: string) {
    try {
      const result: any = await this.http
        .get(`${this.apiUrl}/repos/${owner}/${repo}/collaborators`, {
          headers: this.headers,
        })
        .toPromise();
      return result.find((c: any) => c.login === this.user.login);
    } catch (error) {
      console.error("Error checking if user is a collaborator:", error);
      return false;
    }
  }

  async moveFile(oldPath, newPath) {
    const branch = localStorage.getItem("selectedBranch");
    const baseUrl = `https://api.github.com/repos/${this.repo}/contents`;

    const oldFileResponse: any = await this.http
      .get(`${baseUrl}/${oldPath}`, {
        headers: this.headers,
      })
      .toPromise();

    const content = oldFileResponse.content; // Contenuto codificato in base64
    const sha = oldFileResponse.sha; // SHA del file

    const newFileResponse: any = await this.http
      .put(
        `${baseUrl}/${newPath}`,
        {
          message: `Moving file from ${oldPath} to ${newPath}`,
          content: content, // Lo stesso contenuto base64 del vecchio file
          branch: branch,
        },
        { headers: this.headers }
      )
      .toPromise();

    if (newFileResponse) {
      await this.deleteFile(oldPath, sha);
    }

    this.resyncNeeded.emit();
  }

  // TODO: da testare!
  async moveFolder(oldFolderPath, newFolderPath) {
    const branch = localStorage.getItem("selectedBranch");

    const baseUrl = `https://api.github.com/repos/${this.repo}/contents`;

    const folderResponse: any = await this.http
      .get(`${baseUrl}/${oldFolderPath}`, {
        headers: this.headers,
      })
      .toPromise();

    // Step 2: Iterare sui file della cartella
    for (const item of folderResponse) {
      if (item.type === "file") {
        const oldFilePath = item.path;
        const newFilePath = oldFilePath.replace(oldFolderPath, newFolderPath);
        const fileSha = item.sha;

        // Step 3: Recuperare il contenuto del file
        const fileResponse: any = await this.http
          .get(`${baseUrl}/${oldFilePath}`, {
            headers: this.headers,
          })
          .toPromise();

        const content = fileResponse.content; // Contenuto in base64

        // Step 4: Creare il nuovo file nella nuova cartella
        const newFileResponse = await this.http.put(
          `${baseUrl}/${newFilePath}`,
          {
            message: `Moving file from ${oldFilePath} to ${newFilePath}`,
            content: content, // Contenuto codificato in base64
            branch: branch,
          },
          { headers: this.headers }
        );

        if (newFileResponse) {
          console.log(`File moved to ${newFilePath}`);

          this.deleteFile(oldFilePath, fileSha);
        }
      }
    }
  }

  async getWorkflows() {
    if (!this.repo) {
      return [];
    }
    const url = `https://api.github.com/repos/${this.repo}/actions/workflows`;

    const data: any = await this.http
      .get(url, {
        headers: this.headers,
      })
      .toPromise();

    for (const workflow of data.workflows) {
      if (workflow.path.startsWith("dynamic/")) {
        continue;
      }
      const workflowPath = `${workflow.path}`;
      const content = await this.getFileContent(workflowPath);
      workflow.hasManualTrigger = this.hasManualTrigger(content);
    }

    console.log(data.workflows);

    return data.workflows.filter(
      (w: any) => w.state === "active" && w.hasManualTrigger
    );
  }

  async triggerWorkflow(workflowId: number, branch: string = "master") {
    branch = branch || localStorage.getItem("selectedBranch");

    const url = `https://api.github.com/repos/${this.repo}/actions/workflows/${workflowId}/dispatches`;

    const response = await this.http
      .post(
        url,
        {
          ref: branch, // Branch o commit su cui eseguire l'action
        },
        {
          headers: this.headers,
        }
      )
      .toPromise();

    console.log(`Workflow ${workflowId} triggered successfully`);
  }

  async getFileContent(workflowPath: string) {

    if (!this.repo) {
      return null;
    }

    const url = `https://api.github.com/repos/${this.repo}/contents/${workflowPath}`;

    try {
      const data: any = await this.http
        .get(url, { headers: this.headers })
        .toPromise();
      const content = atobUTF8(data.content); // Decodifica il contenuto base64
      return content;
    } catch (error) {
      return "";
    }
  }

  hasManualTrigger(yamlContent: string): boolean {
    return yamlContent.includes("workflow_dispatch");
  }

  async findAll(search: string) {
    if (!search || search.length < 3) {
      return [];
    }

    const searchUrl = `https://api.github.com/search/code?nonce=${Math.random()}&q=` + encodeURI(`${search}+in:file,path+repo:${this.repo}`);
    const results: any = await this.http
      .get(searchUrl, { headers: this.headers })
      .toPromise();
    return results;
  }

  getFilesInFolder(folder: string) {
    if (!this.repo) {
      return of(null);
    }
    return this.http.get(
      `${this.apiUrl}/repos/${this.repo}/contents/${folder}`,
      {
        headers: this.headers,
      }
    );
  }

  getOwner() {
    return localStorage.getItem("ghostOwner") || this.user.login;
  }

  getRepo() {
    return localStorage.getItem("selectedRepo");
  }

  updateFiles(files: any[], pr = false): Promise<Observable<any>> {
    return new Promise(async (r, e) => {
      if (!pr) {
        r(of(this.commitFiles(files, localStorage.getItem("selectedBranch"))));

      } else {
        r(
          await this.createPR(
            "massive-update-" + Date.now(),
            files.map((f) => ({ path: f.path, content: f.content })),
            `Update ${files.length} files`,
            `This PR updates ${files.length} files: ${files}`
          )
        );
      }
    });
  }

  async createPR(newBranchName, files: any[], title, prMessage) {
    try {
      const headers = this.headers;
      // get the default branch (or use a selected branch)
      const branch = localStorage.getItem("selectedBranch");
      const branchResponse: any = await this.http
        .get(`${this.apiUrl}/repos/${this.repo}/git/ref/heads/${branch}`, {
          headers,
        })
        .toPromise();

      const defaultBranchSha = branchResponse.object.sha;
      const newBranchResponse = await this.http
        .post(
          `${this.apiUrl}/repos/${this.repo}/git/refs`,
          {
            ref: `refs/heads/${newBranchName}`,
            sha: defaultBranchSha,
          },
          { headers }
        )
        .toPromise();

      await this.commitFiles(files, newBranchName);

      // create a pull request to merge the new branch into the selected branch
      const prBody = {
        title,
        head: newBranchName,
        base: branch,
        body: prMessage,
      };

      return this.http.post(`${this.apiUrl}/repos/${this.repo}/pulls`, prBody, {
        headers,
      });
    } catch (error) {
      console.error("Error during file update and PR creation:", error);
      throw error;
    }
  }

  getLastCommitSha(branch: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/repos/${this.repo}/git/refs/heads/${branch}`, {
      headers: this.headers
    });
  }

  // Step 2: Crea un blob per ogni file
  createBlob(content: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/repos/${this.repo}/git/blobs`, {
      content: content + ' ',
      encoding: 'utf-8'
    }, {
      headers: this.headers
    });
  }

  // Step 3: Crea un tree con i blob
  createTree(baseTree: string, files: { path: string, sha: string }[]): Observable<any> {
    const tree = files.map(file => ({
      path: file.path,
      mode: '100644',
      type: 'blob',
      sha: file.sha
    }));

    return this.http.post(`${this.apiUrl}/repos/${this.repo}/git/trees`, {
      base_tree: baseTree,
      tree
    }, {
      headers: this.headers
    });
  }

  // Step 4: Crea un commit
  createCommit(message: string, treeSha: string, parentSha: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/repos/${this.repo}/git/commits`, {
      message,
      tree: treeSha,
      parents: [parentSha]
    }, {
      headers: this.headers
    });
  }

  // Step 5: Aggiorna il branch
  updateBranch(newCommitSha: string, branch: string): Observable<any> {
    return this.http.patch(`${this.apiUrl}/repos/${this.repo}/git/refs/heads/${branch}`, {
      sha: newCommitSha,
      force: false
    }, {
      headers: this.headers
    });
  }

  getTreeSha(commitSha: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/repos/${this.repo}/git/commits/${commitSha}`, {
      headers: this.headers
    });
  }


  // Funzione principale per eseguire il commit
  async commitFiles(files: { path: string, content: string }[], branch: string): Promise<void> {
    try {
      // Step 1: Ottieni l'ultimo commit SHA
      // Step 1: Ottieni l'ultimo commit SHA
      const refData: any = await this.getLastCommitSha(branch).toPromise();
      const lastCommitSha = refData.object.sha;

      // Step 2: Ottieni il tree SHA dell'ultimo commit
      const commitData: any = await this.getTreeSha(lastCommitSha).toPromise();
      const baseTreeSha = commitData.tree.sha;

      // Step 3: Crea un blob per ogni file
      const blobPromises = files.map(file => this.createBlob(file.content).toPromise());
      const blobs = await Promise.all(blobPromises);

      // Step 4: Crea un tree con i nuovi blob
      const treeData: any = await this.createTree(baseTreeSha, files.map((file, index) => ({
        path: file.path,
        sha: blobs[index].sha
      }))).toPromise();

      console.log('Tree creato con i seguenti file:', files);

      // Step 5: Crea un commit
      const newCommitData: any = await this.createCommit('Commit con più file modificati', treeData.sha, lastCommitSha).toPromise();

      // Step 6: Aggiorna il branch
      await this.updateBranch(newCommitData.sha, branch).toPromise();

      console.log('Commit creato con successo:', newCommitData.sha);
    } catch (error) {
      console.error('Errore durante il commit:', error);
    }
  }

  getRepoInfo(): Observable<any> {
    if (!this.repo) {
      return of(null);
    }
    return this.http.get(`${this.apiUrl}/repos/${this.repo}`, { headers: this.headers });
  }

  getRepoStats(): Observable<any> {
    if (!this.repo) {
      return of([]);
    }
    return this.http.get(`${this.apiUrl}/repos/${this.repo}/stats/contributors`, { headers: this.headers });
  }

  getPullRequests(): Observable<any> {
    if (!this.repo) {
      return of(null);
    }
    return this.http.get(`${this.apiUrl}/repos/${this.repo}/pulls?state=all`, { headers: this.headers });
  }

  createFolder(name: string) {
    const obs = this.http.put(`${this.apiUrl}/repos/${this.repo}/contents/${name}/new-file.md`, {
      message: `Create folder ${name}`,
      content: btoaUTF8("Hi there!\n\nI'm a new file in a new folder!"),
    }, {
      headers: this.headers
    }).toPromise();
    this.resyncNeeded.emit({
      path: `${name}/new-file.md`,
      type: 'delete'
    });


    obs.then(() => {
      this.resyncNeeded.emit({
        path: `${name}/new-file.md`,
        type: 'delete'
      });
    });
    return obs;
  }
}
